import { useGtm, type TrackingProduct } from './useGtm/useGtm';
import { useUser } from './useUser/useUser';
import { useCart } from './useCart/useCart';
import { useAppLocale } from './useAppLocale';
import { orderGetters, paymentGetters, productGetters, shippingMethodGetters } from '~/composables/getters';
import type { LineItem, Order, Product, Category, Cart, ShippingMethod, Customer } from '~/types/GraphQL';
import type { ProductTeaser } from '~/components/Product/types/product';

export const useTracking = () => {
  const {
    gtmTracking,
    delayedGtmPageView,
    gtmProductView,
    gtmAddToCart,
  } = useGtm();
  const {
    addPaymentInfo,
    addShippingInfo,
    beginCheckout,
    removeFromCart,
    viewItemList,
    selectItem,
    viewCart,
    purchase,
    pushCustomEvent,
    viewReco,
    viewSearch,
  } = useGA4();
  const { $domain, $times } = useNuxtApp();
  const { locale } = useAppLocale();
  const { isAuthenticated, user } = useUser();
  const { cart } = useCart();
  const { trackPublitasCheckout } = usePublitasTracking();

  const searchQuery = shallowRef<string>();

  const checkout = (order: Order) => {
    if (!order?.lineItems) {
      trackError('checkout', `No line items found in order ${order?.orderNumber}!`);
      return;
    }

    purchase(order);

    // Replace the property values with your actual values
    window.VWO?.event?.('purchase', {
      checkout: true,
      revenue: orderGetters.getFormattedPrice(order.totalPrice),
      paymentMethod: shippingMethodGetters.getShippingDisplayName(order.shippingInfo?.shippingMethod as ShippingMethod, 'de-DE'),
      myKikMember: !!order.customer?.externalId,
      registeredUser: isAuthenticated.value,
      numberOfProducts: order.lineItems.length,
    });

    order.lineItems.forEach((item) => {
      window.VWO.event('productsSold', {
        productId: item.productKey,
        quantity: item.quantity,
      });
    });
    trackPublitasCheckout(order);
  };

  const plpSearch = (internalSearch: string) => {
    if (searchQuery.value === internalSearch) {
      return;
    }

    searchQuery.value = internalSearch;
    pushCustomEvent({ event: 'search', searchTerm: internalSearch });
  };

  const trackBasketOpen = () => {
    gtmTracking({ event: 'open_basket' });
  };

  const trackLoginOpen = () => {
    gtmTracking({ event: 'open_login_layer' });
  };

  const trackForgetPassword = () => {
    gtmTracking({ event: 'forgot_password' });
  };

  const trackForgetPasswordResend = () => {
    gtmTracking({ event: 'forgot_password_resend' });
  };

  const trackLogin = () => {
    gtmTracking({ event: 'login' });
  };

  const trackStartRegistration = () => {
    gtmTracking({ event: 'start_registration' });
  };

  const trackStartCheckout = () => {
    if (cart.value?.lineItems) {
      beginCheckout(cart.value?.lineItems);
    }
  };

  const trackError = (type: string, message: string) => {
    pushCustomEvent({ event: 'error', errorMessage: message, errorType: type });
  };

  const trackRemoveItem = (item: LineItem) => {
    removeFromCart(item);
  };

  const trackProductView = (item: TrackingProduct) => {
    gtmProductView(item);
  };

  const trackNavigation = () => {
    if (!window.UC_UI) {
      return;
    }

    const statuses: Record<string, boolean> = (window.UC_UI.getServicesBaseInfo() as any[])?.reduce((acc, { name, consent }) => ({ ...acc, [name]: consent.status }), {});

    gtmTracking({ ...statuses, event: 'consent_status' });
  };

  const trackPage = () => {
    delayedGtmPageView(user.value as Customer, isAuthenticated.value);
  };

  const trackAddToCart = (item: TrackingProduct, data: { sku: string; quantity: number }) => {
    gtmAddToCart(item, data.sku);
  };

  function addDays(date: Date, days: number) {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() + days);
    return newDate;
  }

  const trackTrustedShops = (order: Order) => {
    const tsCheckoutProducts = order.lineItems?.map((item) => {
      const productUrl = productGetters.getUrl(item, { locale });

      return {
        tsCheckoutProductUrl: `https://${$domain.host}${productUrl}`,
        tsCheckoutProductImageUrl: item.variant?.images?.[0].url,
        tsCheckoutProductName: item.name,
        tsCheckoutProductSKU: item.productKey,
      };
    });

    gtmTracking(
      {
        event: 'trusted_shops_data',
        // eslint-disable-next-line camelcase
        ts_checkout: {
          tsCheckoutOrderNr: order?.orderNumber,
          tsCheckoutBuyerEmail: order?.billingAddress?.email,
          tsCheckoutOrderAmount: order && order.totalPrice.centAmount / 100,
          tsCheckoutOrderCurrency: $domain.currency,
          tsCheckoutOrderPaymentType: paymentGetters.getActivePaymentMethod(order?.paymentInfo)?.paymentMethodInfo.method,
          tsCheckoutProducts,
          ...($times.deliveryTime ? { tsCheckoutOrderEstDeliveryDate: addDays(new Date(), $times.deliveryTime).toISOString().slice(0, 10) } : {}),
        },
      },
    );
  };

  const trackAddPaymentInfo = (paymentMethod: string) => {
    if (!cart.value?.lineItems) {
      trackError('payment info', 'no items in cart');
      return;
    }

    addPaymentInfo(paymentMethod, cart.value?.lineItems);
  };

  const trackAddShippingInfo = (shippingMethod: string) => {
    if (!cart.value?.lineItems) {
      trackError('payment info', 'no items in cart');
      return;
    }

    addShippingInfo(shippingMethod, cart.value?.lineItems);
  };

  const trackViewCart = (cart: Cart) => {
    viewCart(cart);
  };

  const trackRegistered = () => {
    pushCustomEvent({ event: 'registration' });
  };

  const trackViewItemList = (category: Category, products: Product[] | ProductTeaser[]) => {
    viewItemList(category, products);
  };

  const trackViewSearch = (searchTerm: string, products: Product[] | ProductTeaser[], filters: any) => {
    viewSearch(searchTerm, products, filters);
  };

  const trackViewReco = (category: Category, products: Product[] | ProductTeaser[], type: string) => {
    viewReco(category, products, type);
  };

  const trackSelectItem = (categoryInfo?: Category, product?: Product) => {
    selectItem(categoryInfo, product);
  };

  const trackForms = (id: string) => {
    pushCustomEvent({ event: 'formSubmit', formId: id });
  };

  return {
    // GTM
    gtmTracking,
    // misc
    checkout,
    plpSearch,
    trackBasketOpen,
    trackLoginOpen,
    trackForgetPassword,
    trackForgetPasswordResend,
    trackLogin,
    trackStartRegistration,
    trackStartCheckout,
    trackError,
    trackRemoveItem,
    trackProductView,
    trackAddToCart,
    trackPage,
    trackNavigation,
    trackTrustedShops,
    // GA4
    trackAddPaymentInfo,
    trackAddShippingInfo,
    trackViewCart,
    trackRegistered,
    trackViewItemList,
    trackViewReco,
    trackSelectItem,
    trackForms,
    trackViewSearch,
  };
};
